import AccordionCustom from "@components/Accordion/AccordionCustom";
import FeatureVideoCardSection from "@components/FeatureVideoCard";
import ImageGrid from "@components/ImageGrid";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import FeatureCardSection from "@components/LandingPageComponents/CardSection";
import OdooHeader from "@components/LandingPageComponents/OdooHeader";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import dynamic from "next/dynamic";
import Head from "next/head";
import goireland from "public/goireland.png";
import React from "react";

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/fb-leads/card1.png",
        title: "Sync Facebook Lead Details with WhatsApp",
        description: "Move all your lead information from Facebook to WhatsApp to engage and sustain their interest with personalised responses",
    },
    {
        img: "https://assets.gallabox.com/gb-home/personalized-card.png",
        title: "Enhance Engagement with Media Elements",
        description: "Make your conversations engaging with media elements (images, videos, and more) to boost engagement and other metrics",
    },
    {
        img: "https://assets.gallabox.com/gb-home/fb-leads/card3.png",
        title: "Identify Prospects and Nurture Them Properly",
        description: "Use the details captured in the Facebook Leads form to identify hot leads and send tailor-made responses that nudge them to buy ",
    },
    {
        img: "https://assets.gallabox.com/gb-home/fb-leads/card4.png",
        title: " Assess the Quality of Your Facebook Leads",
        description: "Qualify your Facebook leads effectively using custom variables, a predefined questionnaire, an AI-powered chatbot, and more",
    },
];

const CardDataCarousal = dynamic(() => import("@components/LandingPageComponents/CardDataCarousal"), {
    ssr: false,
});

const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "How do I convert Facebook leads to WhatsApp contacts?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Gallabox's Facebook Lead Forms and WhatsApp integration enable businesses to effectively import, store, manage, engage, and connect with existing and new Facebook prospects on WhatsApp–all without any manual effort or coding.",
            },
        },

        {
            "@type": "Question",
            name: "Why integrate Facebook Lead Forms with WhatsApp?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "By moving your Facebook leads to WhatsApp, you can:1.Sync lead info and custom form variables instantly,2.Eliminate manual errors, redundancy, and data silos,3.Have personalised conversations with prospects,4.Improve conversions with tailor-made offers,5.Get a bird’s eye view of your lead quality and performance.",
            },
        },
        {
            "@type": "Question",
            name: "How to integrate Facebook Leads and WhatsApp Business??",
            acceptedAnswer: {
                "@type": "Answer",
                text: "To integrate WhatsApp with your Facebook Leads, you need a WhatsApp Automation Solution like Gallabox. Gallabox offers a one-tap integration which will connect your WhatsApp Business API with your Facebook Lead Forms instantly.To use the FB Leads integration, simply connect your Facebook page to Gallabox and create a lead ad. Gallabox will then automatically create a contact for each person who submits their information through the lead form and store all the details collected through the form.",
            },
        },
    ],
};

const facebook: React.FC = () => {
    return (
        <NewLayout
            title="Facebook Leads to WhatsApp Integration | Gallabox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/fb-leads/fb-og.png" }}
            canonical="https://gallabox.com/whatsapp-integrations/facebook-leads-to-whatsapp"
            description="Automate lead conversion by connecting Facebook and Instagram leads to WhatsApp. Start two-way conversations instantly and close more sales."
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
            </Head>
            <div className="bg-white">
                <OdooHeader
                    pageTitle="WhatsApp Facebook Lead Ads Integration"
                    mainTitle="Facebook Leads to WhatsApp Integration for Better Conversion"
                    description="Accelerate your sales cycle by sharing your Facebook lead details with WhatsApp to connect, send nudges, and convert them faster."
                    buttonText="Sign Up Today"
                    imageUrl="https://assets.gallabox.com/gb-home/fb-leads/header.png"
                />

                <div className="lg:max-w-7xl mx-auto">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>

                <CardDataCarousal cardDataCollection={cardDataCollection} />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/fb-leads/section1.png"
                    title="Import Your Facebook Leads Into WhatsApp Easily"
                    description="With the Gallabox and Facebook Lead Ads Integration, you can now automatically capture all prospect information from Facebook Lead Forms. Save the time and effort you spend moving Facebook leads to your WhatsApp Business account."
                    isImageRightSide
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/fb-leads/section2.png"
                    title="Add a Human Touch to Your WhatsApp Templates"
                    description="Use the custom variables collected in Facebook Lead Forms to send targeted offers and messages to your leads on WhatsApp. You can convert them organically using our AI-rewrite to turn your canned responses into candid replies."
                />
                <FeatureVideoCardSection
                    isVideo
                    mediaURL="https://assets.gallabox.com/gb-home/fb-leads/section3.mp4"
                    title="Set Auto Responders and Drip Marketing Campaigns"
                    description="Use the “Add Actions” option to tag, segment, and set up a relevant drip marketing sequence on WhatsApp. Gallabox Sequence can help you with this, whether you need to nurture leads who are not ready to buy or follow up with existing users for an upsell."
                    isImageRightSide
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/fb-leads/section4.png"
                    title="Make Data-Backed Decisions with Messaging Metrics"
                    description="Easily track the performance of your Facebook campaigns and understand which ones are delivering the best results with delivery and read reports. Use this information to make informed decisions and optimise your Facebook campaigns accordingly."
                />

                <YellowCTAhref
                    buttonText="Get Started Today"
                    customText={
                        <h3 className="text-2xl md:text-3xl font-bold text-yellow-800">
                            Ready to Make the Most Out of Your <br /> Facebook Lead Forms?
                        </h3>
                    }
                />

                <TestimonialSection
                    TestimonialSectionPropsData={[
                        {
                            companyImg: goireland,
                            headerTitle: "Gallabox’s WhatsApp Widget reduced FTR  from 15-30 minutes to less than a minute, boosting engagement and conversion rates.",
                            avatarImg: "https://assets.gallabox.com/gb-home/go-ireland/goireland-owner.png",
                            nameText: "Ilaya Bharathi",
                            professionText: "Founder & CEO, GoIreland",
                        },
                    ]}
                />

                <ImageGrid />

                <section className="max-w-6xl py-4 mx-auto px-4">
                    <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                        <p className="lg:text-4xl text-3xl text-center font-bold text-gray-700">Frequently asked questions</p>
                        <h3 className="text-gray-500 text-center lg:text-2xl text-lg font-normal ">Everything you need to know about FB Leads-Gallabox integration</h3>
                    </div>
                    <AccordionCustom
                        title="MoEngage-Gallabox integration"
                        items={[
                            {
                                question: "How do I convert Facebook leads to WhatsApp contacts??",
                                answer: "Gallabox's Facebook Lead Forms and WhatsApp integration enable businesses to effectively import, store, manage, engage, and connect with existing and new Facebook prospects on WhatsApp–all without any manual effort or coding.",
                            },
                            {
                                question: "Why integrate Facebook Lead Forms with WhatsApp?",
                                answer: (
                                    <>
                                        <span>{"By moving your Facebook leads to WhatsApp, you can:"}</span>

                                        <ol className="pl-1 pt-2">
                                            <li>{"Sync lead info and custom form variables instantly."}</li>
                                            <li>{"Eliminate manual errors, redundancy, and data silos."}</li>
                                            <li>{"Have personalised conversations with prospects."}</li>
                                            <li>{"Improve conversions with tailor-made offers."}</li>
                                            <li>{"Get a bird’s eye view of your lead quality and performance."}</li>
                                        </ol>
                                    </>
                                ),
                            },
                            {
                                question: "How to integrate Facebook Leads and WhatsApp Business?",
                                answer: (
                                    <>
                                        <span>
                                            {
                                                "To integrate WhatsApp with your Facebook Leads, you need a WhatsApp Automation Solution like Gallabox. Gallabox offers a one-tap integration which will connect your WhatsApp Business API with your Facebook Lead Forms instantly."
                                            }
                                        </span>
                                        <span className=" pt-4">
                                            {
                                                "To use the FB Leads integration, simply connect your Facebook page to Gallabox and create a lead ad. Gallabox will then automatically create a contact for each person who submits their information through the lead form and store all the details collected through the form."
                                            }
                                        </span>
                                    </>
                                ),
                            },
                        ]}
                    />
                </section>
            </div>
        </NewLayout>
    );
};

export default facebook;
